import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { ENFORCEMENT_LABELS } from '../mock';
import { usePolicies, useUpdatePolicy } from '../api/hooks';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import NewPolicyDialog from '../components/NewPolicyDialog';
import { ShieldCheck, Bot, AlertTriangle, Plus } from 'lucide-react';
import { toast } from '../lib/aiToast';

const ENF = ['off', 'warn', 'enforce'];

// Policy module — governs how agents act across workflows. Distinct from
// Settings → Automation (which agents run); policies set the guardrails.
export default function Policies() {
  const { session } = useAuth();
  const practiceId = session?.practice?.id || 'prac_001';
  const { data: policies = [] } = usePolicies(practiceId);
  const upd = useUpdatePolicy();
  const [dialogOpen, setDialogOpen] = useState(false);

  const toggle = (id) => {
    const p = policies.find((x) => x.id === id);
    if (!p) return;
    upd.mutate({ id, enabled: !p.enabled });
    toast.message(`${p.name} ${!p.enabled ? 'enabled' : 'paused'}`);
  };
  const setEnf = (id, enforcement) => upd.mutate({ id, enforcement });

  const enforced = policies.filter((p) => p.enabled && p.enforcement === 'enforce').length;
  const violations = policies.reduce((s, p) => s + (p.enabled ? p.violations : 0), 0);

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Governance" title="Policies" subtitle="Organization rules that govern how agents act across every workflow — independent of your PM software.">
        <Link to="/app/settings"><Button variant="outline"><Bot className="h-4 w-4 mr-1.5" />Automation rules</Button></Link>
        <Button onClick={() => setDialogOpen(true)} className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />New policy</Button>
      </PageHeader>

      <NewPolicyDialog open={dialogOpen} onOpenChange={setDialogOpen} existing={policies} />

      <div className="ai-surface p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="flex items-center gap-2 text-sm text-slate-700"><ShieldCheck className="h-4 w-4 text-brand-500" /><b className="tnum">{enforced}</b> policies enforced</div>
        <div className="flex items-center gap-2 text-sm text-slate-700"><AlertTriangle className="h-4 w-4 text-amber-500" /><b className="tnum">{violations}</b> violations flagged this week</div>
        <div className="text-xs text-slate-500 ml-auto">Off = inactive · Warn = alert staff · Enforce = block until satisfied</div>
      </div>

      <div className="space-y-3">
        {policies.map((p) => (
          <div key={p.id} className="dh-card p-4 flex items-start gap-4">
            <div className="pt-0.5"><Switch checked={p.enabled} onCheckedChange={() => toggle(p.id)} /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-sm font-semibold text-slate-900">{p.name}</div>
                <span className="pill pill-slate">{p.scope}</span>
                {p.enabled && p.violations > 0 && <span className="pill pill-amber"><AlertTriangle className="h-3 w-3" />{p.violations} flagged</span>}
              </div>
              <div className="text-xs text-slate-500 mt-1">{p.description}</div>
            </div>
            <div className="shrink-0">
              <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                {ENF.map((e) => {
                  const active = p.enforcement === e;
                  const tone = active
                    ? (e === 'enforce' ? 'bg-brand-600 text-white shadow-sm' : e === 'warn' ? 'bg-amber-500 text-white shadow-sm' : 'bg-slate-500 text-white shadow-sm')
                    : 'text-slate-500 hover:text-slate-800';
                  return (
                    <button key={e} disabled={!p.enabled} onClick={() => setEnf(p.id, e)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${tone} ${!p.enabled ? 'opacity-40' : ''}`}>
                      {ENFORCEMENT_LABELS[e]}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
